import { useEffect } from 'react';

export interface LevelClearOverlayProps {
  level: number;
  onDone: () => void;
}

const DISPLAY_MS = 1200;

/** PART 3: brief, auto-dismissing "LEVEL N" banner. Clearing all 244
 * pickups previously snapped silently to a fresh board mid-frame with zero
 * on-screen acknowledgement -- this is layered on top the same way
 * CountdownOverlay is (see its own comment): purely cosmetic, never gates
 * or derives a second copy of the already-server-authoritative state
 * underneath (SPEC.md §1.1). The CALLER (GameScreen.tsx) owns deciding
 * WHEN to mount this -- keyed strictly on `roundClearedCount` increasing
 * (game/levelClear.ts's `didLevelClear`), so it fires exactly once per
 * real level clear and never on a pause/resume or a same-snapshot
 * re-render. The scrim reuses `.overlay` (same alpha as every other
 * overlay -- do NOT darken it, see index.css's own comment on that rule);
 * the "flash" itself is a CSS animation on `.level-clear-label`, disabled
 * entirely under `prefers-reduced-motion` (index.css). */
export function LevelClearOverlay({ level, onDone }: LevelClearOverlayProps): JSX.Element {
  useEffect(() => {
    const t = setTimeout(onDone, DISPLAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="overlay level-clear-overlay" role="status" aria-live="polite" data-testid="level-clear-overlay">
      <div className="level-clear-label">LEVEL {level}</div>
    </div>
  );
}
